/**
 * Templates a system administrator has published to companies.
 * Company admins add a template unchanged as a draft, then add events and release (or Edit criteria on the cycle row when editable).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { translateUi } from "../i18n/index";
import { CheckCircle2, Eye, Plus, ShieldCheck } from "../icons";
import { cb, cbCn } from "./cycleBuilderTheme";
import { CompanyTemplateWorkflowFlow } from "./CompanyTemplateWorkflowFlow";
import { TemplateCriteriaPreviewModal } from "./TemplateCriteriaPreviewModal";
import type { LibraryCycle } from "./cycleLibraryUi";

interface LibraryElement {
  id: string;
  title: string;
  demonstrate?: string[];
  explain?: string[];
}

interface LibraryTemplate {
  id: string;
  name: string;
  expiryYears?: number;
  template_editable_by_company?: number;
  units?: Array<{ id: string; title: string; elements?: LibraryElement[] }>;
}

function countCriteria(tpl: LibraryTemplate): number {
  let total = 0;
  for (const unit of tpl.units ?? []) {
    for (const el of unit.elements ?? []) {
      total += (el.demonstrate?.length ?? 0) + (el.explain?.length ?? 0);
    }
  }
  return total;
}

export function CompanyTemplatesPane({
  companyCode,
  currentUserEmail,
  isDarkMode = false,
  isMobile = false,
  embedded = false,
  inWizard = false,
  onCountChange,
  onMounted,
  onApplied,
}: {
  companyCode?: string;
  currentUserEmail?: string | null;
  isDarkMode?: boolean;
  isMobile?: boolean;
  /** Skip outer panel chrome when nested in CollapsiblePane. */
  embedded?: boolean;
  /** Inside the cycle wizard template step — uses a 3-column tile grid. */
  inWizard?: boolean;
  onCountChange?: (count: number) => void;
  onMounted?: (refresh: () => void) => void;
  /** Applied a template, creating a company cycle managers can assign. */
  onApplied?: () => void;
}) {
  const [templates, setTemplates] = useState<LibraryTemplate[]>([]);
  const [appliedIds, setAppliedIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [applyingId, setApplyingId] = useState<string | null>(null);
  const [previewTemplate, setPreviewTemplate] = useState<LibraryTemplate | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const headers: Record<string, string> = {};
      if (currentUserEmail) headers["X-User-Email"] = currentUserEmail;
      if (companyCode?.trim()) {
        headers["X-Company-Code"] = companyCode.trim();
        headers["X-Cycle-Library-Company-View"] = "1";
      }
      const [libRes, appliedRes] = await Promise.all([
        fetch(`/api/cycle-library?v=${Date.now()}`, { headers, cache: "no-store" }),
        companyCode
          ? fetch(`/api/cycles/company-list?companyCode=${encodeURIComponent(companyCode)}&v=${Date.now()}`, {
              headers,
              cache: "no-store",
            })
          : Promise.resolve(null),
      ]);
      if (!libRes.ok) {
        setError("Could not load the available templates. Refresh the page and try again.");
        return;
      }
      const lib = await libRes.json().catch(() => []);
      const list: LibraryTemplate[] = Array.isArray(lib) ? lib : [];
      setTemplates(list);
      onCountChange?.(list.length);
      if (appliedRes && appliedRes.ok) {
        // A template counts as applied once the company owns a cycle built from it.
        const rows = await appliedRes.json().catch(() => []);
        setAppliedIds(
          (Array.isArray(rows) ? rows : [])
            .filter((r: { company_code?: string | null }) => !!r?.company_code)
            .map((r: { framework_cycle_id?: string | null }) => String(r?.framework_cycle_id ?? "").trim())
            .filter(Boolean)
        );
      }
    } catch {
      setError("Could not load the available templates. Refresh the page and try again.");
    } finally {
      setLoading(false);
    }
  }, [companyCode, currentUserEmail, onCountChange]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    onMounted?.(() => void refresh());
  }, [onMounted, refresh]);

  const applied = useMemo(() => new Set(appliedIds), [appliedIds]);

  const handleApply = async (tpl: LibraryTemplate) => {
    if (!companyCode || !currentUserEmail) return;
    setApplyingId(tpl.id);
    setError(null);
    setNotice(null);
    try {
      const res = await fetch(`/api/companies/${encodeURIComponent(companyCode)}/apply-framework?v=${Date.now()}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-User-Email": currentUserEmail, "Cache-Control": "no-cache" },
        body: JSON.stringify({ cycleId: tpl.id, cycleName: tpl.name }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        setAppliedIds((prev) => (prev.includes(tpl.id) ? prev : [...prev, tpl.id]));
        setNotice(
          translateUi(
            '"{{title}}" was added to your cycles as a draft. Add assessment events, then release it to managers when ready.',
            { title: tpl.name }
          )
        );
        onApplied?.();
      } else {
        setError(data.error ?? "Failed to apply this template");
      }
    } catch {
      setError("Failed to apply this template");
    } finally {
      setApplyingId(null);
    }
  };

  const body = (
    <div className="space-y-4">
      {!embedded && !inWizard && (
        <div>
          <h3 className={cb.heading(isDarkMode, !isMobile)}>{translateUi("Templates available to your company")}</h3>
          <p className={cbCn("mt-1 text-sm", cb.body(isDarkMode))}>
            {translateUi(
              "Add a template unchanged as a draft. Then add assessment events on the cycle row and release to managers. Editable templates can be customised with Edit on the cycle row."
            )}
          </p>
        </div>
      )}

      {error && <div className={cbCn("rounded-lg border px-3 py-2 text-sm", cb.alertError(isDarkMode))}>{error}</div>}
      {notice && (
        <div className={cbCn("rounded-lg border px-3 py-2 text-sm", cb.alertSuccess(isDarkMode))} role="status">
          {notice}
        </div>
      )}

      {loading ? (
        <p className={cbCn("py-6 text-center text-sm", cb.bodySm(isDarkMode))}>{translateUi("Loading…")}</p>
      ) : templates.length === 0 ? (
        <p className={cbCn(cb.muted(isDarkMode), "block")}>
          {inWizard
            ? translateUi("No templates have been published yet. Go back and choose Build custom instead.")
            : translateUi("No templates have been published yet. You can still build a custom cycle below.")}
        </p>
      ) : (
        <>
          <div className="space-y-4">
            <CompanyTemplateWorkflowFlow isDarkMode={isDarkMode} />
        <ul
          className={cbCn(
            "grid gap-3",
            inWizard
              ? isMobile
                ? "grid-cols-1"
                : "sm:grid-cols-2 lg:grid-cols-3"
              : isMobile
                ? "grid-cols-1"
                : "sm:grid-cols-2"
          )}
        >
          {templates.map((tpl) => {
            const isApplied = applied.has(tpl.id);
            const units = tpl.units?.length ?? 0;
            const criteria = countCriteria(tpl);
            const editable = Number(tpl.template_editable_by_company ?? 0) === 1;
            return (
              <li
                key={tpl.id}
                className={cbCn(
                  "flex flex-col gap-3 rounded-xl border p-4",
                  isApplied ? cb.listRow(isDarkMode) : cb.templateRow(isDarkMode)
                )}
              >
                <div className="min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <p className={cbCn("truncate text-sm font-semibold", isDarkMode ? "text-slate-100" : "text-slate-900")}>
                      {tpl.name}
                    </p>
                    <button
                      type="button"
                      onClick={() => setPreviewTemplate(tpl)}
                      className={cbCn(
                        "inline-flex shrink-0 items-center gap-1 rounded-lg px-2 py-1 text-[11px] font-semibold",
                        cb.btnSecondary(isDarkMode)
                      )}
                      title={translateUi("View all units and criteria in this template")}
                    >
                      <Eye className="h-3.5 w-3.5" />
                      <span className="hidden sm:inline">{translateUi("View criteria")}</span>
                    </button>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    <span className={cb.tag(isDarkMode, "indigo")}>
                      {units} {units === 1 ? translateUi("unit") : translateUi("units")}
                    </span>
                    <span className={cb.tag(isDarkMode, "slate")}>
                      {criteria} {criteria === 1 ? translateUi("criterion") : translateUi("criteria")}
                    </span>
                    {tpl.expiryYears ? (
                      <span className={cb.tag(isDarkMode, "slate")}>
                        {tpl.expiryYears} {tpl.expiryYears === 1 ? translateUi("year") : translateUi("years")}
                      </span>
                    ) : null}
                    <span className={cb.tag(isDarkMode, editable ? "emeraldSoft" : "amber")}>
                      {editable ? translateUi("You can edit the criteria") : translateUi("Fixed criteria")}
                    </span>
                  </div>
                </div>
                <div className="mt-auto space-y-2">
                  <span className={cbCn("inline-flex items-center gap-1.5 text-xs", cb.bodySm(isDarkMode))}>
                    <ShieldCheck className="h-3.5 w-3.5 shrink-0" />
                    {translateUi("Rail Intel template")}
                  </span>
                  <div className="flex justify-end">
                    {isApplied ? (
                      <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-600">
                        <CheckCircle2 className="h-4 w-4 shrink-0" />
                        {translateUi("Already in your cycles")}
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => void handleApply(tpl)}
                        disabled={!companyCode || !currentUserEmail || applyingId === tpl.id}
                        title={translateUi("Adds this template unchanged to your cycles as a draft. Add assessment events, then release to managers.")}
                        className={cbCn(
                          "inline-flex w-full items-center justify-center gap-1.5 whitespace-nowrap rounded-lg px-3 py-2 text-xs font-semibold sm:w-auto",
                          cb.btnPrimary(!companyCode || !currentUserEmail || applyingId === tpl.id)
                        )}
                      >
                        <Plus className="h-3.5 w-3.5 shrink-0" />
                        {applyingId === tpl.id ? translateUi("Adding…") : translateUi("Add template")}
                      </button>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
          </div>
        </>
      )}
    </div>
  );

  const previewCycle = previewTemplate
    ? ({
        id: previewTemplate.id,
        name: previewTemplate.name,
        expiryYears: previewTemplate.expiryYears,
        template_editable_by_company: previewTemplate.template_editable_by_company,
        units: (previewTemplate.units ?? []).map((u) => ({
          id: u.id,
          title: u.title,
          elements: (u.elements ?? []).map((el) => ({
            id: el.id,
            title: el.title,
            demonstrate: el.demonstrate ?? [],
            explain: el.explain ?? [],
          })),
        })),
      } satisfies LibraryCycle)
    : null;

  const modal = (
    <TemplateCriteriaPreviewModal
      open={!!previewTemplate}
      cycle={previewCycle}
      locked={Number(previewTemplate?.template_editable_by_company ?? 0) !== 1}
      isDarkMode={isDarkMode}
      onClose={() => setPreviewTemplate(null)}
    />
  );

  if (embedded) {
    return (
      <div className={isMobile ? "p-4" : "p-5 sm:p-6"}>
        {body}
        {modal}
      </div>
    );
  }
  return (
    <>
      <div className={cb.panel(isDarkMode, isMobile ? "sm" : "md")}>{body}</div>
      {modal}
    </>
  );
}
